// components/consumers/complaints/ComplaintsTab.tsx

import { useConsumerComplaints } from "@features/cx/consumerManagement/hooks";
import { Complaint } from "@features/cx/consumerManagement/types";
import { GenericTable } from "@shared/components/GenericTable";
import { SortConfig, TableAction, TableColumn } from "@shared/types/table";
import { Button } from "@shared/ui/button";
import { PAGE_SIZE } from "@shared/utils/constants";
import { Eye } from "lucide-react";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

interface ComplaintsTabProps {
  consumerId: string;
  remoteUtilityId: number;
}

export const ComplaintsTab: React.FC<ComplaintsTabProps> = ({
  consumerId,
  remoteUtilityId,
}) => {
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const { data, isLoading, error } = useConsumerComplaints(consumerId, {
    page: currentPage,
    limit: PAGE_SIZE,
    query: searchTerm,
    sort_field: sortConfig?.field,
    sort_direction: sortConfig?.direction,
    remote_utility_id: remoteUtilityId,
  });

  const navigate = useNavigate();
  const details = data?.results;

const flattenedComplaints = useMemo(() => {
  if (!details) return [];

  return details.map(({ id, utilitySupportRequest, ...rest }) => {
    const { id: _, ...utilityRest } = utilitySupportRequest || {}; // remove id from nested object
    return {
      id,
      ...rest,
      ...utilityRest, // spread without the nested id
      priorityLevelDisplay: utilitySupportRequest?.extraData?.priorityLevelDisplay ?? "Unknown", // Fix: access from utilitySupportRequest
    };
  });
}, [details]);

  // Define table columns
  const columns: TableColumn<Complaint>[] = [
    {
      key: "requestNo",
      header: "Complaint ID",
      sortable: true,
    },
    {
      key: "name",
      header: "Complaint Name",
      sortable: true,
    },
    {
      key: "supportRequestType",
      header: "Category",
      sortable: true,
    },
    {
      key: "supportRequestSubtype",
      header: "Sub Category",
      sortable: true,
    },
    {
      key: "statusDisplay",
      header: "Status",
      sortable: true,

      type: "badge",
      badgeConfig: {
        getVariant: (value: string) => {
          switch (value) {
            case "COMPLETED":
              return "default";
            case "PENDING":
              return "secondary";
            case "CREATED":
              return "outline";
            case "CANCELLED":
              return "destructive";
            default:
              return "default";
          }
        },
        getClassName: (value: string) => {
          switch (value) {
            case "COMPLETED":
              return "bg-green-100 text-green-800 border-green-200 hover:bg-green-200";
            case "CREATED":
              return "bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-200";
            case "PENDING":
              return "bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-200";
            case "CANCELLED":
              return "bg-red-100 text-red-800 border-red-200 hover:bg-red-200";
            default:
              return "bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200";
          }
        },
      },
    },
    {
      key: "priorityLevelDisplay",
      header: "Priority",
      sortable: true,
      type: "badge",
      badgeConfig: {
        getVariant: (value: string) => {
          switch (value) {
            case "High":
              return "destructive";
            case "Medium":
              return "secondary";
            case "Low":
              return "outline";
            default:
              return "default";
          }
        },
        getClassName: (value: string) => {
          switch (value) {
            case "High":
              return "bg-red-100 text-red-800 border-red-200 hover:bg-red-200";
            case "Medium":
              return "bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-200";
            case "Low":
              return "bg-green-100 text-green-800 border-green-200 hover:bg-green-200";
            default:
              return "bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200";
          }
        },
      },
    },
    {
      key: "createdDate",
      header: "Created On",
      sortable: true,
    },
  ];

  const actions: TableAction<any>[] = [
    {
      label: "View",
      icon: <Eye className="h-4 w-4" />,
      onClick: (row: any) => {
        console.log("debug row click", row);
        row.statusDisplay === "COMPLETED" || row.statusDisplay === "REJECTED"
          ? navigate(`/cx/complaints/history/${row.id}`)
          : navigate(`/cx/complaints/current/${row.id}`);
      },
    },
  ];

  // Handle sorting
  const handleSort = (field: string, direction: "asc" | "desc") => {
    setSortConfig({ field, direction });
    setCurrentPage(1);
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle search
  const handleSearch = (searchValue: string, paramName: string) => {
    setSearchTerm(searchValue);
    setCurrentPage(1);
  };

  // Handle row selection
  const handleSelectionChange = (newSelectedRows: string[]) => {
    setSelectedRows(newSelectedRows);
  };

  // Error state
  if (error) {
    return (
      <div className="text-center py-8 text-red-600">
        <p>{error.message}</p>
        <Button
          onClick={() => {
            setCurrentPage(1);
          }}
          className="mt-2"
          variant="outline"
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Generic Table */}
      <GenericTable
        data={flattenedComplaints}
        columns={columns}
        actions={actions}
        actionsType="icons"
        rowKey="id"
        selectable={false}
        selectedRows={selectedRows}
        onSelectionChange={handleSelectionChange}
        pagination={{
          currentPage: data?.page || 1,
          totalPages: data?.total_pages || 1,
          totalItems: data?.count || 0,
          itemsPerPage: data?.limit || 10,
          startItem: data?.page ? (data.page - 1) * (data.limit || 10) + 1 : 0,
          endItem: data?.page
            ? Math.min(data.page * (data.limit || 10), data.count || 0)
            : 0,
        }}
        onPageChange={handlePageChange}
        sortConfig={sortConfig}
        onSort={handleSort}
        searchPlaceholder="Search complaints..."
        searchParamName="search"
        searchDebounceMs={500}
        onSearch={handleSearch}
        emptyMessage="No complaints found"
        getRowClassName={(row, index) => {
          return row.status === 1 ? "bg-red-50" : "";
        }}
      />
    </div>
  );
};
